import type { Card, Envelope, ShEvent, ShTableState, Suit } from "../gen/bazivan/v1/table_pb";
import { client } from "../net/client";
import { signal } from "../net/signal";
import { Pace } from "../net/pace";
import { showCarpet } from "../game/carpet";
import { Sequencer } from "../scene/queue";
import { introGate } from "../game/stake";
import { buildProof, cardCode, DealWatch, type DealProof } from "../game/dealLock";
import { BreakWatch, RESULT_EVENTS } from "../game/handBreak";
import { botText } from "../game/seats";
import {
  applyShelem, askOf, bidTags, canPlay, emptyShelem, MAX_BID, sameCard, ShPhase, toLocal, trickPoints, unitOf, WIDOW,
  type ShAsk, type ShelemLocal,
} from "./state";
import { cutKindOf } from "../hokm/state";
import { asHokmView } from "./view";
import type { HokmScene } from "../hokm/scene";

/**
 * پایانِ دست یا مسابقه — پنجره‌ی نتایج را ری‌اکت می‌کشد.
 *
 * `declUnit` واحدِ محلیِ **حاکم** است (WinnerUnit ِ رویدادِ hand_over همان واحدِ حاکم است، نه برنده)؛
 * `shelem` هم شلمِ حاکم را دارد و هم شلمِ حریف — کدامش، از امتیازِ دست پیداست.
 */
export type ShOverlay =
  | { kind: "hand"; declUnit: number; bid: number; roundScore: number[]; shelem: boolean; kot: boolean; yasa: boolean }
  | { kind: "match"; winnerUnit: number };

/**
 * کنترلرِ میزِ آنلاینِ شلم.
 *
 * هیچ قاعده‌ای این‌جا اجرا نمی‌شود؛ حقیقت همیشه ShTableStateِ سرور است. سه کار:
 * ترجمه‌ی وضعیت، انیمیتِ رویدادها به ترتیب، و تبدیلِ لمسِ بازیکن به فرمان.
 *
 * فرقش با حکم و چهاربرگ: بازیکن چهار جور ورودی دارد و نه یکی — خواندن، پاس،
 * واگذاریِ چهار برگ، اعلامِ حکم، و انداختنِ کارت. کدامشان باز باشد را **سرور**
 * می‌گوید (awaiting*FromMe از روی phase)، نه حدسِ کلاینت؛ وگرنه صفحه چیزی را
 * می‌پرسید که میز نمی‌پذیرفت.
 */
export class ShelemOnline {
  private seq = new Sequencer();
  private scene: HokmScene;
  private off: (() => void) | null = null;
  private gotState = false;
  private seatsLaid = false;
  private quitting = false;
  /** دو‌بار‌زدنِ همان خواندن یا «پاس»: دومی فقط «نوبتِ تو نیست» می‌گرفت — net/pace.ts. */
  private tap = new Pace(0, 1, 600);
  private carpetApplied = false;
  private ceremonyDone = false;
  private startShown = false;
  private st: ShelemLocal = emptyShelem();
  private turnEndsAt = 0;
  private turnTotal = 0;
  private tick: ReturnType<typeof setInterval> | null = null;

  readonly overlay = signal<ShOverlay | null>(null);
  /** مکثِ میانِ دو دست — «ادامه» ِ همه ([[BreakWatch]]). */
  readonly brk = new BreakWatch();
  /** قفلِ پخشِ دستی که تمام شد — [[DealProof]]؛ null = نشانی نیست. */
  readonly proof = signal<DealProof | null>(null);
  private deal = new DealWatch();
  readonly raw = signal<ShTableState | null>(null);
  /**
   * آنچه میز همین حالا از من می‌پرسد. با لمس همان لحظه null می‌شود و نه با وضعیتِ بعدیِ سرور:
   * تا آن برسد پنجره باز می‌ماند و لمسِ دوم «نوبتِ تو نیست» یا «الان وقتش نیست» می‌گرفت (پنجره‌ی
   * حکم حتی تا پایانِ اعلام و «شروع!» روی میز بود). سرور که رد کند، دوباره پرسیده می‌شود.
   */
  readonly ask = signal<ShAsk>(null);
  /** چهار برگِ زمین که همین حالا به دستِ حاکم آمد — نشانشان در دست. */
  private widow: Card[] | null = null;
  /** برگ‌هایی که حاکم برای واگذاری انتخاب کرده؛ در دست بالا می‌آیند. */
  readonly picked = signal<Card[]>([]);
  /** خواندنِ آخرِ هر صندلیِ محلی در همین مزایده — برچسبِ کنارِ صندلی ([[bidTags]]). */
  private said: (number | null)[] = [];

  constructor(scene: HokmScene) {
    this.scene = scene;
    scene.setCompactPiles(true);
  }
  get state(): ShelemLocal { return this.st; }

  start(): void {
    this.off = client.on((env) => this.onMessage(env));
    // پرده‌ی شرط بالاست؟ صحنه تا بالا رفتنش چیزی نمی‌کشد — [[CbOnline.start]].
    this.seq.hold(introGate());
    const ace = client.shAce.value;
    if (ace) this.seq.enqueue(() => this.playEvent(ace));
    const deal = client.shDeal.value;
    if (deal) this.seq.enqueue(() => this.playEvent(deal));
    const w = client.shWidow.value;
    if (w) this.seq.enqueue(() => this.playEvent(w));
    const last = client.shTable.value;
    if (last) this.seq.enqueueSnapshot(() => this.applyState(last));
    this.tick = setInterval(() => this.updateTimer(), 200);
    (window as unknown as { __shelem?: ShelemOnline }).__shelem = this;
  }

  dispose(): void {
    this.quitting = true;
    this.off?.(); this.off = null;
    this.seq.dispose();
    if (this.tick) clearInterval(this.tick);
  }

  // ---------------------------------------------------------------- ورودیِ بازیکن

  /** خواندن. سرور دوباره می‌سنجدش؛ این‌جا فقط جلوی دکمه‌ی بی‌معنا گرفته می‌شود. */
  bid(amount: number): void {
    if (this.quitting || this.ask.value !== "bid") return;
    if (amount < this.st.bidFloor || amount > MAX_BID || amount % 5 !== 0) return;
    if (!this.tap.take("bid:" + amount)) return;
    this.answered();
    client.shBid(amount);
  }

  pass(): void {
    if (this.quitting || this.ask.value !== "bid") return;
    if (this.st.mustBid) { this.scene.toast("شروع‌کننده باید بخوانَد"); return; }
    if (!this.tap.take("pass")) return;
    this.answered();
    client.shPass();
  }

  /** لمسِ کارت: در واگذاری انتخاب/لغوِ انتخاب، در بازی انداختن. */
  clickCard(i: number): void {
    if (this.quitting) return;
    const card = this.st.myHand[i];
    if (!card) return;

    if (this.st.awaitingDiscardFromMe) {
      if (this.ask.value !== "discard") return;   // چهار برگ رفته؛ تا پاسخِ سرور دست نخورَد
      const cur = this.picked.value;
      const at = cur.findIndex((c) => sameCard(c, card));
      if (at >= 0) this.picked.value = cur.filter((_, k) => k !== at);
      else if (cur.length >= 4) { this.scene.toast("چهار برگ بس است — یکی را بزن تا پایین برود"); return; }
      else this.picked.value = [...cur, card];
      this.refreshPlayable();
      return;
    }

    if (!this.st.myTurn) return;
    if (!canPlay(this.st, card)) { this.scene.toast("باید از خال زمینه بیایی"); return; }
    // تا پاسخِ سرور دوباره‌فرستادن بسته است — [[HokmOnline.clickCard]]. بی این، لمسِ دوم
    // کارتِ دیگری می‌فرستاد و «نوبتِ تو نیست» می‌گرفت.
    this.st.myTurn = false;
    this.refreshPlayable();
    this.answered();
    client.shPlay(card);
  }

  /** واگذاریِ چهار برگِ انتخاب‌شده. */
  discard(): void {
    if (this.quitting || this.ask.value !== "discard") return;
    const p = this.picked.value;
    if (p.length !== 4) { this.scene.toast("چهار برگ انتخاب کن"); return; }
    // برگ‌ها تا وضعیتِ بعدی بالا می‌مانند و همان‌جا از دست می‌روند — نه این‌که اول پایین بیفتند.
    this.answered();
    client.shDiscard(p);
  }

  callTrump(suit: Suit): void {
    if (this.quitting || this.ask.value !== "trump") return;
    this.answered();
    client.shTrump(suit);
  }

  /** فرمان رفت: پنجره و ساعتِ نوبت همین حالا می‌روند، نه با وضعیتِ بعدی. */
  private answered(): void {
    this.ask.value = null;
    this.turnEndsAt = 0; this.turnTotal = 0;
    this.scene.setTimer(0, 0);
  }

  /** سرور فرمان را رد کرد: آنچه پیش از لمس پرسیده می‌شد دوباره پرسیده شود. */
  private reask(): void {
    const ts = this.raw.value;
    if (!ts || this.quitting) return;
    const fresh = applyShelem(ts);
    this.st.myTurn = fresh.myTurn;
    this.ask.value = askOf(fresh);
    this.refreshPlayable();
  }

  /** «ادامه» — [[HokmOnline.nextHand]]. */
  nextHand(): void { if (!this.brk.press()) this.overlay.value = null; }
  seatName(l: number): string { return l === 0 ? "تو" : botText(this.st.names[l] || "حریف", this.st.bots[l]); }

  // ---------------------------------------------------------------- پیام‌های سرور

  private onMessage(env: Envelope): void {
    if (this.quitting) return;
    switch (env.msg.case) {
      case "shState": { const ts = env.msg.value; this.seq.enqueueSnapshot(() => this.applyState(ts), this.brk.mustShow(ts.brk)); break; }
      case "shEvent": { const ev = env.msg.value; this.seq.enqueue(() => this.playEvent(ev), RESULT_EVENTS.has(ev.kind)); break; }
      case "error": this.showError(env.msg.value.code); break;
    }
  }

  private showError(code: string): void {
    const msg: Record<string, string> = {
      not_your_turn: "نوبت تو نیست",
      not_in_hand: "این کارت در دستت نیست",
      follow_suit: "باید از خال زمینه بیایی",
      low_bid: "باید بالاتر از خواندن فعلی بخوانی",
      bid_step: "خواندن پله‌ی پنج‌تایی دارد",
      bid_range: "خواندن بین ۱۰۰ و ۱۶۵ است",
      must_bid: "شروع‌کننده باید بخوانَد",
      already_passed: "پاس داده‌ای",
      not_declarer: "این کار مال حاکم است",
      discard_count: "چهار برگ باید بگذاری",
      discard_dup: "یک کارت را دو بار نمی‌شود گذاشت",
      wrong_phase: "الان وقتش نیست",
    };
    if (msg[code]) this.scene.toast(msg[code]!);
    this.reask();
  }

  private async applyState(ts: ShTableState): Promise<void> {
    // پنلِ نتیجه دستِ کم PANEL_MIN_MS دیده شود، حتی اگر این صفحه پشتِ سرور مانده — [[BreakWatch.keep]].
    await this.brk.keep(this.seq, ts.brk, this.overlay.value?.kind === "hand");
    if (this.quitting) return;
    this.st = applyShelem(ts);
    this.raw.value = ts;
    this.brk.update(ts.brk, ts.seats, ts.me, ts.watching);
    if (!ts.watching) this.deal.observe(ts.dealLock, ts.myHand.map((c) => cardCode(c)));
    this.gotState = true;

    if (!this.carpetApplied && this.st.carpet) { showCarpet(this.st.carpet); this.carpetApplied = true; }
    const v = asHokmView(this.st);
    if (!this.seatsLaid) { this.scene.layoutSeats(v); this.seatsLaid = true; }
    this.scene.render(v);
    this.scene.renderTrick(v);
    this.scene.updateScores(v);
    this.scene.setTurn(v);
    // مدالِ حکم از هر وضعیت از نو ساخته می‌شود — [[HokmScene.syncTrump]].
    this.scene.syncTrump(this.st.trumpChosen, this.st.trump);
    // زمین تا پایانِ مزایده وسطِ میز است — از هر وضعیت، تا برگشت از قطعی هم درستش ببیند.
    this.scene.setWidow(this.st.phase === ShPhase.BID && !this.st.handOver && !this.st.matchOver ? WIDOW : 0);

    // انتخابِ واگذاری فقط تا وقتی که سرور هنوز می‌پرسد معنا دارد.
    if (!this.st.awaitingDiscardFromMe && this.picked.value.length > 0) this.picked.value = [];
    if (this.st.phase !== ShPhase.DISCARD && this.widow) this.widow = null;
    this.ask.value = askOf(this.st);
    this.refreshPlayable();
    this.refreshTags();

    if (this.overlay.value && !this.st.handOver && !this.st.matchOver) this.overlay.value = null;

    const asking = this.st.myTurn || this.st.awaitingBidFromMe ||
      this.st.awaitingDiscardFromMe || this.st.awaitingTrumpFromMe;
    if (asking && this.st.turnRemaining > 0.1) {
      this.turnTotal = Math.max(this.turnTotal, this.st.turnRemaining);
      this.turnEndsAt = performance.now() + this.st.turnRemaining * 1000;
    } else { this.turnEndsAt = 0; this.turnTotal = 0; this.scene.setTimer(0, 0); }
  }

  private refreshPlayable(): void {
    if (this.st.awaitingDiscardFromMe) {
      // در واگذاری هر برگی برداشتنی است؛ قابِ «بازی‌شدنی» روی هر شانزده‌تا فقط شلوغی بود. به‌جایش
      // برگِ برداشته بالا می‌آید و برگِ زمین نشان دارد.
      const has = (set: Card[] | null) => (c: Card) => !!set?.some((x) => sameCard(x, c));
      this.scene.markPlayable(this.st.myHand.map(() => false));
      this.scene.markDiscard({ picked: this.st.myHand.map(has(this.picked.value)), widow: this.st.myHand.map(has(this.widow)) });
      return;
    }
    this.scene.markDiscard(null);
    if (!this.st.myTurn) { this.scene.markPlayable(this.st.myHand.map(() => false)); return; }
    this.scene.markPlayable(this.st.myHand.map((c) => canPlay(this.st, c)));
  }

  /** برچسب‌های مزایده کنارِ صندلی‌ها — فقط سرِ مزایده. */
  private refreshTags(): void {
    const live = this.st.phase === ShPhase.BID && !this.st.handOver && !this.st.matchOver;
    this.scene.setSeatTags(live ? bidTags(this.st, this.said) : null);
  }

  private updateTimer(): void {
    if (this.turnEndsAt <= 0) return;
    const left = (this.turnEndsAt - performance.now()) / 1000;
    if (left <= 0) { this.turnEndsAt = 0; this.scene.setTimer(0, 0); return; }
    this.scene.setTimer(left, Math.max(left, this.turnTotal));
  }

  private async playEvent(ev: ShEvent): Promise<void> {
    const n = this.st.n;
    const local = toLocal(ev.seat, this.st.me, n);
    switch (ev.kind) {
      case "ace_draw": {
        if (this.ceremonyDone || ev.draw.length === 0) return;
        this.ceremonyDone = true;
        const fresh = client.shTable.value;
        if (fresh) { this.st = applyShelem(fresh); this.raw.value = fresh; this.gotState = true; }
        const v = asHokmView(this.st);
        if (!this.seatsLaid) { this.scene.layoutSeats(v); this.seatsLaid = true; }
        this.scene.hideHand(true);
        this.scene.render(v);
        const from = toLocal(ev.fromSeat, this.st.me, n);
        // مراسم درنگِ خودخواسته است، نه عقب‌ماندگی — [[Sequencer.linger]]. پخش از پیش پشتش در
        // صف است؛ بی این، بعدِ ده ثانیه آس‌کشی «کهنه» دیده می‌شد و پخش و «شروع!» تا چهاربرابر
        // تند می‌آمدند (آزمونِ prod، ۲۸ اکتبر ۲۰۲۶: ۱٫۹ ثانیه به‌جای ~۷ ثانیه).
        await this.seq.linger((async () => {
          await this.scene.animStarterDraw(ev.draw, from, local, this.seatName(local));
          await this.scene.animShuffle();
        })());
        this.scene.hideHand(false);
        this.scene.render(asHokmView(this.st));
        client.shAce.value = null;
        break;
      }

      case "deal": {
        // دستِ تازه با همین رویداد شروع می‌شود، پیش از وضعیتش: پنلِ نتیجه همین‌جا می‌ماند تا دیده شود،
        // و بعد همین‌جا بسته می‌شود — وگرنه پخشِ دستِ تازه (در کندترین سرعت ~۸ ثانیه) زیرِ پنل می‌گذشت.
        await this.brk.keep(this.seq, undefined, this.overlay.value?.kind === "hand");
        if (this.quitting) return;
        if (this.overlay.value?.kind === "hand") this.overlay.value = null;
        const fresh = client.shTable.value;
        if (fresh) { this.st = applyShelem(fresh); this.raw.value = fresh; this.gotState = true; }
        if (!this.gotState) break;
        const v = asHokmView(this.st);
        if (!this.carpetApplied && this.st.carpet) { showCarpet(this.st.carpet); this.carpetApplied = true; }
        if (!this.seatsLaid) { this.scene.layoutSeats(v); this.seatsLaid = true; }
        this.said = [];
        this.refreshTags();
        await this.scene.animDealBatch(v, local, Math.min(12, Math.max(1, this.st.myHand.length || 12)));
        await this.scene.animWidowDeal(WIDOW);
        client.shDeal.value = null;
        break;
      }

      // خواندن و پاس کنارِ خودِ صندلی می‌مانند ([[HokmScene.setSeatTags]])، نه توستی که بالای میز
      // روی چهره‌ی بالانشین می‌نشست. وضعیتِ بعدی همین‌ها را از سرور درست می‌کند.
      case "bid":
        if (local < 0 || local >= n) break;
        this.said[local] = ev.amount;
        this.st.highBid = ev.amount; this.st.highSeat = local;
        this.refreshTags();
        break;
      case "pass":
        if (local < 0 || local >= n) break;
        this.st.passed[local] = true;
        this.refreshTags();
        break;
      case "declarer":
        this.scene.toast(`حاکم ${this.seatName(local)} با ${fa(ev.amount)}`);
        this.scene.setSeatTags(null);
        if (local >= 0 && local < n) await this.scene.animWidowTake(local);
        break;

      case "widow":
        // فقط به خودِ حاکم می‌رسد؛ این چهار برگ در دست نشان می‌گیرند تا بداند مزایده چه به او داد.
        this.widow = [...ev.cards];
        client.shWidow.value = null;
        this.refreshPlayable();
        break;

      case "discard":
        if (local === 0) this.widow = null;
        break;

      case "trump_called": {
        await this.scene.animTrumpAnnounce(ev.suit);
        if (!this.startShown) { this.startShown = true; await this.scene.animStartBanner("شروع!"); }
        break;
      }

      case "played": {
        if (!ev.card) return;
        const card = ev.card;
        // بریدن پیش از نشستنِ کارت سنجیده می‌شود — همان تیغه‌ی حکم ([[HokmScene.animCut]]).
        const cut = cutKindOf(asHokmView(this.st), card);
        if (local === 0 && !this.st.watching) {
          const at = this.st.myHand.findIndex((c) => sameCard(c, card));
          if (at >= 0) this.st.myHand.splice(at, 1);
          this.st.counts[0] = this.st.myHand.length;
        } else if (local >= 0 && local < n && (this.st.counts[local] ?? 0) > 0) this.st.counts[local]!--;
        this.scene.render(asHokmView(this.st));
        await this.scene.animPlayToTrick(local, card);
        if (this.st.trickLeader < 0) { this.st.trickLeader = local; this.st.ledSuit = card.suit; this.st.trickHasLead = true; }
        this.st.trick[local] = card;
        this.scene.renderTrick(asHokmView(this.st));
        if (cut.kind !== "none") await this.scene.animCut(asHokmView(this.st), local, cut.kind, cut.order);
        break;
      }

      case "trick_won": {
        if (local < 0 || local >= n) return;
        this.scene.popPoints(local, trickPoints(this.st.trick), unitOf(local) === 0);
        await this.scene.animCollectTrick(asHokmView(this.st), local);
        this.st.trick.fill(null);
        this.st.trickLeader = -1;
        this.st.trickHasLead = false;
        this.scene.renderTrick(asHokmView(this.st));
        break;
      }

      case "hand_over":
        // دسته‌ها: چهار صندلی و زمین (۴). حاکم زمین را برمی‌دارد، پس کارتش هم مجاز است.
        this.proof.value = this.st.watching ? null : await buildProof(this.deal, ev.dealSecret, ev.dealHands, 4,
          this.st.me, (s) => toLocal(s, this.st.me, 4), "ورق‌ها پیش از بازی مهر شد و کسی به آن‌ها دست نزد.", { pile: 4, name: "زمین", mayTake: true });
        this.overlay.value = {
          kind: "hand", declUnit: localUnit(ev.winnerUnit, this.st.me),
          bid: ev.amount, roundScore: rotate(ev.roundScore, this.st.me),
          shelem: ev.shelem, kot: ev.kot, yasa: ev.yasa,
        };
        break;

      case "match_over":
        this.overlay.value = { kind: "match", winnerUnit: localUnit(ev.winnerUnit, this.st.me) };
        break;

      case "seat_bot":
        this.scene.toast(`${this.seatName(local)} رفت — ربات جایش نشست`);
        break;
      case "seat_reconnect":
        this.scene.toast(`${this.seatName(local)} برگشت`);
        break;
    }
  }
}

const UNITS = 2;
const localUnit = (serverUnit: number, me: number) => ((serverUnit - (me % UNITS)) % UNITS + UNITS) % UNITS;

/** امتیازِ دور را به ترتیبِ واحدِ محلی می‌چیند. */
function rotate(score: number[], me: number): number[] {
  const out = new Array<number>(UNITS).fill(0);
  for (let u = 0; u < UNITS; u++) out[localUnit(u, me)] = score[u] ?? 0;
  return out;
}

const faDigits = "۰۱۲۳۴۵۶۷۸۹";
const fa = (n: number) => String(n).replace(/\d/g, (d) => faDigits[+d]!);
